'use client';

import { c as _c } from "react/compiler-runtime";
import Image from 'next/image'; // ALLOW-RAW-IMG: static decorative placeholder, not user content
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { FAKE_REFERRAL_SUMMARY, USE_FAKE_REFERRAL_DATA } from '@/components/portfolio/referrals/_fake-data';
import { EarningsSparkline, type SparklineRange } from '@/components/portfolio/referrals/earnings-sparkline';
import { ReferralsHowItWorksDialog } from '@/components/portfolio/referrals/referrals-how-it-works-dialog';
import { ShowRefereesDialog } from '@/components/portfolio/referrals/show-referees-dialog';
import { useGetReferral } from '@/components/portfolio/referrals/stub-hooks';
import { PortfolioPanelHeader } from '@/components/portfolio/shared/portfolio-panel-header';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Skeleton } from '@/components/ui/app-skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/app-tabs';
import { AppTooltip } from '@/components/ui/app-tooltip';
import { Icon } from '@/components/ui/icon';
import { ExpandIcon, HelpCircleIconSolid } from '@/components/ui/icon/glyphs';
import { Panel, PanelContent } from '@/components/ui/panel';
import { useFormattedCurrency } from '@/lib/currency';
import { withDemoMode } from '@/lib/network';
import { useUserNetwork } from '@/lib/network/client';
import { resolveCanonicalOrigin } from '@/lib/seo';
import { ShareModal } from '@/lib/share';
const PANEL_HEIGHT_CLASS: string = 'min-h-[320px]'; // ARBITRARY: spec-mandated minimum (Figma 3396:209) — flex-grow expands beyond this naturally.

const RANGES: ReadonlyArray<{
  key: SparklineRange;
  labelKey: 'chart_7d' | 'chart_30d' | 'chart_90d' | 'chart_all';
}> = [{
  key: '7d',
  labelKey: 'chart_7d'
}, {
  key: '30d',
  labelKey: 'chart_30d'
}, {
  key: '90d',
  labelKey: 'chart_90d'
}, {
  key: 'all',
  labelKey: 'chart_all'
}];
interface ReferralsPanelProps {
  /** UUID of the portfolio owner — gated to own-viewer at the call site. */
  userId: string;
}
export function ReferralsPanel(t0) {
  const $ = _c(193);
  const {
    userId
  } = t0;
  const t = useTranslations("profile.next.summary.referrals");
  const tSocial = useTranslations("social.referrals");
  const {
    formatD6
  } = useFormattedCurrency();
  const {
    network
  } = useUserNetwork();
  const [range, setRange] = useState("30d");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  let t1;
  if ($[0] !== tSocial) {
    t1 = tSocial("how_it_works");
    $[0] = tSocial;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setHelpOpen(true);
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== helpOpen || $[4] !== t1) {
    t3 = {
      label: t1,
      onClick: t2,
      expanded: helpOpen
    };
    $[3] = helpOpen;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const infoTrigger = t3;
  const hasUserId = userId.length > 0;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      fields: ["userId", "l1Count", "l2Count", "totalL1Earnings", "totalL2Earnings", "combinedEarnings"]
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== hasUserId) {
    t5 = {
      enabled: hasUserId
    };
    $[7] = hasUserId;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const {
    data,
    status
  } = useGetReferral(userId, t4, t5);
  let t6;
  if ($[9] !== network || $[10] !== userId) {
    const u = new URL(`/r/${userId}`, resolveCanonicalOrigin());
    withDemoMode(u, network === "mainnet" ? "mainnet" : "devnet");
    t6 = u.toString();
    $[9] = network;
    $[10] = userId;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const url = t6;
  const isLoading = USE_FAKE_REFERRAL_DATA ? false : status === "loading" || status === "idle";
  if (isLoading) {
    let t7;
    let t8;
    let t9;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <Skeleton className="h-5 w-28" />;
      t8 = <Skeleton className="h-8 w-32" />;
      t9 = <Skeleton className="h-20 w-full rounded-card" />;
      $[12] = t7;
      $[13] = t8;
      $[14] = t9;
    } else {
      t7 = $[12];
      t8 = $[13];
      t9 = $[14];
    }
    let t10;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <Panel className={PANEL_HEIGHT_CLASS}><PanelContent className="flex h-full flex-col gap-3">{t7}{t8}{t9}<div className="grid grid-cols-2 gap-2"><Skeleton className="h-12 w-full rounded-panel-inset" /><Skeleton className="h-12 w-full rounded-panel-inset" /></div><Skeleton className="mt-auto h-9 w-full rounded-control" /></PanelContent></Panel>;
      $[15] = t10;
    } else {
      t10 = $[15];
    }
    return t10;
  }
  let t7;
  if ($[16] !== data) {
    t7 = USE_FAKE_REFERRAL_DATA ? FAKE_REFERRAL_SUMMARY : data ?? {};
    $[16] = data;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const summary = t7;
  const l1Count = Number(summary.l1Count ?? 0);
  const l2Count = Number(summary.l2Count ?? 0);
  const total = l1Count + l2Count;
  const hasReferrals = total > 0;
  const totalL1Earnings = String(summary.totalL1Earnings ?? "0");
  const totalL2Earnings = String(summary.totalL2Earnings ?? "0");
  const availableToClaim = String(summary.availableToClaim ?? summary.combinedEarnings ?? "0");
  const claimable = Number(availableToClaim) > 0;
  if (!hasReferrals) {
    let t8;
    if ($[18] !== t) {
      t8 = t("title_label");
      $[18] = t;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    let t9;
    if ($[20] !== infoTrigger || $[21] !== t8) {
      t9 = <PortfolioPanelHeader title={t8} info={infoTrigger} />;
      $[20] = infoTrigger;
      $[21] = t8;
      $[22] = t9;
    } else {
      t9 = $[22];
    }
    let t10;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <Image src="/portfolio/referrals-placeholder.png" alt="" width={72} height={72} className="size-[72px]" />;
      $[23] = t10;
    } else {
      t10 = $[23];
    }
    let t11;
    if ($[24] !== t) {
      t11 = t("empty_title");
      $[24] = t;
      $[25] = t11;
    } else {
      t11 = $[25];
    }
    let t12;
    if ($[26] !== t11) {
      t12 = <h3 className="text-heading-md">{t11}</h3>;
      $[26] = t11;
      $[27] = t12;
    } else {
      t12 = $[27];
    }
    let t13;
    if ($[28] !== t) {
      t13 = t("empty_body");
      $[28] = t;
      $[29] = t13;
    } else {
      t13 = $[29];
    }
    let t14;
    if ($[30] !== t13) {
      t14 = <p className="text-body text-foreground-secondary">{t13}</p>;
      $[30] = t13;
      $[31] = t14;
    } else {
      t14 = $[31];
    }
    let t15;
    if ($[32] !== t12 || $[33] !== t14) {
      t15 = <div className="flex flex-col items-center gap-2 text-center">{t10}<div className="flex flex-col items-center gap-3">{t12}{t14}</div></div>;
      $[32] = t12;
      $[33] = t14;
      $[34] = t15;
    } else {
      t15 = $[34];
    }
    let t16;
    if ($[35] !== url) {
      t16 = <code className="flex h-9 flex-1 items-center truncate rounded-control bg-background-tertiary px-3 text-caption text-foreground-secondary">{url}</code>;
      $[35] = url;
      $[36] = t16;
    } else {
      t16 = $[36];
    }
    let t17;
    if ($[37] !== userId) {
      t17 = {
        kind: "referral_link",
        userId
      };
      $[37] = userId;
      $[38] = t17;
    } else {
      t17 = $[38];
    }
    let t18;
    if ($[39] !== t) {
      t18 = t("share_text");
      $[39] = t;
      $[40] = t18;
    } else {
      t18 = $[40];
    }
    let t19;
    if ($[41] !== t) {
      t19 = t("share_modal_title");
      $[41] = t;
      $[42] = t19;
    } else {
      t19 = $[42];
    }
    let t20;
    if ($[43] !== t) {
      t20 = t("share_cta");
      $[43] = t;
      $[44] = t20;
    } else {
      t20 = $[44];
    }
    let t21;
    if ($[45] !== t20) {
      t21 = <AnimatedButton variant="primary" className="shrink-0">{t20}</AnimatedButton>;
      $[45] = t20;
      $[46] = t21;
    } else {
      t21 = $[46];
    }
    let t22;
    if ($[47] !== t17 || $[48] !== t18 || $[49] !== t19 || $[50] !== t21 || $[51] !== url) {
      t22 = <ShareModal surface={t17} url={url} text={t18} title={t19} trigger={t21} />;
      $[47] = t17;
      $[48] = t18;
      $[49] = t19;
      $[50] = t21;
      $[51] = url;
      $[52] = t22;
    } else {
      t22 = $[52];
    }
    let t23;
    if ($[53] !== t16 || $[54] !== t22) {
      t23 = <div className="flex w-full items-center gap-2">{t16}{t22}</div>;
      $[53] = t16;
      $[54] = t22;
      $[55] = t23;
    } else {
      t23 = $[55];
    }
    let t24;
    if ($[56] !== t15 || $[57] !== t23) {
      t24 = <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4">{t15}{t23}</div>;
      $[56] = t15;
      $[57] = t23;
      $[58] = t24;
    } else {
      t24 = $[58];
    }
    let t25;
    if ($[59] !== t24 || $[60] !== t9) {
      t25 = <Panel className={PANEL_HEIGHT_CLASS}><PanelContent className="flex h-full flex-col">{t9}{t24}</PanelContent></Panel>;
      $[59] = t24;
      $[60] = t9;
      $[61] = t25;
    } else {
      t25 = $[61];
    }
    let t26;
    if ($[62] !== helpOpen) {
      t26 = <ReferralsHowItWorksDialog open={helpOpen} onOpenChange={setHelpOpen} />;
      $[62] = helpOpen;
      $[63] = t26;
    } else {
      t26 = $[63];
    }
    let t27;
    if ($[64] !== t25 || $[65] !== t26) {
      t27 = <>{t25}{t26}</>;
      $[64] = t25;
      $[65] = t26;
      $[66] = t27;
    } else {
      t27 = $[66];
    }
    return t27;
  }
  const showReferees = total > 0;
  let t8;
  if ($[67] !== t) {
    t8 = t("title_label");
    $[67] = t;
    $[68] = t8;
  } else {
    t8 = $[68];
  }
  let t9;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = v => setRange(v as SparklineRange);
    $[69] = t9;
  } else {
    t9 = $[69];
  }
  let t10;
  if ($[70] !== tSocial) {
    t10 = RANGES.map(t11 => {
      const {
        key,
        labelKey
      } = t11;
      return <TabsTrigger key={key} value={key}>{tSocial(labelKey)}</TabsTrigger>;
    });
    $[70] = tSocial;
    $[71] = t10;
  } else {
    t10 = $[71];
  }
  let t11;
  if ($[72] !== t10) {
    t11 = <TabsList variant="segmented" className="w-auto">{t10}</TabsList>;
    $[72] = t10;
    $[73] = t11;
  } else {
    t11 = $[73];
  }
  let t12;
  if ($[74] !== range || $[75] !== t11) {
    t12 = <Tabs value={range} onValueChange={t9}>{t11}</Tabs>;
    $[74] = range;
    $[75] = t11;
    $[76] = t12;
  } else {
    t12 = $[76];
  }
  let t13;
  if ($[77] !== infoTrigger || $[78] !== t12 || $[79] !== t8) {
    t13 = <PortfolioPanelHeader title={t8} info={infoTrigger} action={t12} />;
    $[77] = infoTrigger;
    $[78] = t12;
    $[79] = t8;
    $[80] = t13;
  } else {
    t13 = $[80];
  }
  let t14;
  if ($[81] !== availableToClaim || $[82] !== formatD6) {
    t14 = formatD6(availableToClaim);
    $[81] = availableToClaim;
    $[82] = formatD6;
    $[83] = t14;
  } else {
    t14 = $[83];
  }
  let t15;
  if ($[84] !== t14) {
    t15 = <span className="text-heading-lg font-bold tabular-nums text-foreground-primary">{t14}</span>;
    $[84] = t14;
    $[85] = t15;
  } else {
    t15 = $[85];
  }
  let t16;
  if ($[86] !== t) {
    t16 = t("available_to_claim");
    $[86] = t;
    $[87] = t16;
  } else {
    t16 = $[87];
  }
  let t17;
  if ($[88] !== t16) {
    t17 = <span className="text-body text-foreground-tertiary">{t16}</span>;
    $[88] = t16;
    $[89] = t17;
  } else {
    t17 = $[89];
  }
  let t18;
  if ($[90] !== t15 || $[91] !== t17) {
    t18 = <div className="flex min-w-0 flex-col gap-0.5">{t15}{t17}</div>;
    $[90] = t15;
    $[91] = t17;
    $[92] = t18;
  } else {
    t18 = $[92];
  }
  let t19;
  if ($[93] !== range || $[94] !== userId) {
    t19 = <EarningsSparkline userId={userId} range={range} />;
    $[93] = range;
    $[94] = userId;
    $[95] = t19;
  } else {
    t19 = $[95];
  }
  let t20;
  if ($[96] !== t) {
    t20 = t("tier_direct_tooltip");
    $[96] = t;
    $[97] = t20;
  } else {
    t20 = $[97];
  }
  let t21;
  if ($[98] !== t) {
    t21 = t("tier_info_button_label", {
      tier: t("tier_direct_label")
    });
    $[98] = t;
    $[99] = t21;
  } else {
    t21 = $[99];
  }
  let t22;
  if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Icon icon={HelpCircleIconSolid} className="size-3" aria-hidden="true" />;
    $[100] = t22;
  } else {
    t22 = $[100];
  }
  let t23;
  if ($[101] !== t21) {
    t23 = <button type="button" aria-label={t21} className="absolute right-2 top-2 text-foreground-tertiary transition-colors hover:text-foreground-secondary">{t22}</button>;
    $[101] = t21;
    $[102] = t23;
  } else {
    t23 = $[102];
  }
  let t24;
  if ($[103] !== t20 || $[104] !== t23) {
    t24 = <AppTooltip side="top" content={t20}>{t23}</AppTooltip>;
    $[103] = t20;
    $[104] = t23;
    $[105] = t24;
  } else {
    t24 = $[105];
  }
  let t25;
  if ($[106] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <span aria-hidden="true" className="size-2 shrink-0 rounded-full bg-[var(--outcome-green-400)]" />;
    $[106] = t25;
  } else {
    t25 = $[106];
  }
  let t26;
  if ($[107] !== t) {
    t26 = t("tier_direct_label");
    $[107] = t;
    $[108] = t26;
  } else {
    t26 = $[108];
  }
  let t27;
  if ($[109] !== t26) {
    t27 = <span className="flex items-center gap-1.5 text-body text-foreground-tertiary">{t25}{t26}</span>;
    $[109] = t26;
    $[110] = t27;
  } else {
    t27 = $[110];
  }
  let t28;
  if ($[111] !== formatD6 || $[112] !== totalL1Earnings) {
    t28 = formatD6(totalL1Earnings);
    $[111] = formatD6;
    $[112] = totalL1Earnings;
    $[113] = t28;
  } else {
    t28 = $[113];
  }
  let t29;
  if ($[114] !== t28) {
    t29 = <span className="text-body-lg font-medium tabular-nums text-foreground-primary">{t28}</span>;
    $[114] = t28;
    $[115] = t29;
  } else {
    t29 = $[115];
  }
  let t30;
  if ($[116] !== t24 || $[117] !== t27 || $[118] !== t29) {
    t30 = <div className="relative flex flex-col gap-0.5 rounded-panel-inset border border-border-secondary bg-background-primary px-4 py-3">{t24}{t27}{t29}</div>;
    $[116] = t24;
    $[117] = t27;
    $[118] = t29;
    $[119] = t30;
  } else {
    t30 = $[119];
  }
  let t31;
  if ($[120] !== t) {
    t31 = t("tier_indirect_tooltip");
    $[120] = t;
    $[121] = t31;
  } else {
    t31 = $[121];
  }
  let t32;
  if ($[122] !== t) {
    t32 = t("tier_info_button_label", {
      tier: t("tier_indirect_label")
    });
    $[122] = t;
    $[123] = t32;
  } else {
    t32 = $[123];
  }
  let t33;
  if ($[124] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <Icon icon={HelpCircleIconSolid} className="size-3" aria-hidden="true" />;
    $[124] = t33;
  } else {
    t33 = $[124];
  }
  let t34;
  if ($[125] !== t32) {
    t34 = <button type="button" aria-label={t32} className="absolute right-2 top-2 text-foreground-tertiary transition-colors hover:text-foreground-secondary">{t33}</button>;
    $[125] = t32;
    $[126] = t34;
  } else {
    t34 = $[126];
  }
  let t35;
  if ($[127] !== t31 || $[128] !== t34) {
    t35 = <AppTooltip side="top" content={t31}>{t34}</AppTooltip>;
    $[127] = t31;
    $[128] = t34;
    $[129] = t35;
  } else {
    t35 = $[129];
  }
  let t36;
  if ($[130] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <span aria-hidden="true" className="size-2 shrink-0 rounded-full bg-[var(--outcome-blue-400)]" />;
    $[130] = t36;
  } else {
    t36 = $[130];
  }
  let t37;
  if ($[131] !== t) {
    t37 = t("tier_indirect_label");
    $[131] = t;
    $[132] = t37;
  } else {
    t37 = $[132];
  }
  let t38;
  if ($[133] !== t37) {
    t38 = <span className="flex items-center gap-1.5 text-body text-foreground-tertiary">{t36}{t37}</span>;
    $[133] = t37;
    $[134] = t38;
  } else {
    t38 = $[134];
  }
  let t39;
  if ($[135] !== formatD6 || $[136] !== totalL2Earnings) {
    t39 = formatD6(totalL2Earnings);
    $[135] = formatD6;
    $[136] = totalL2Earnings;
    $[137] = t39;
  } else {
    t39 = $[137];
  }
  let t40;
  if ($[138] !== t39) {
    t40 = <span className="text-body-lg font-medium tabular-nums text-foreground-primary">{t39}</span>;
    $[138] = t39;
    $[139] = t40;
  } else {
    t40 = $[139];
  }
  let t41;
  if ($[140] !== t35 || $[141] !== t38 || $[142] !== t40) {
    t41 = <div className="relative flex flex-col gap-0.5 rounded-panel-inset border border-border-secondary bg-background-primary px-4 py-3">{t35}{t38}{t40}</div>;
    $[140] = t35;
    $[141] = t38;
    $[142] = t40;
    $[143] = t41;
  } else {
    t41 = $[143];
  }
  let t42;
  if ($[144] !== t30 || $[145] !== t41) {
    t42 = <div className="grid grid-cols-2 gap-4">{t30}{t41}</div>;
    $[144] = t30;
    $[145] = t41;
    $[146] = t42;
  } else {
    t42 = $[146];
  }
  let t43;
  if ($[147] !== sheetOpen || $[148] !== showReferees || $[149] !== t || $[150] !== total) {
    t43 = showReferees ? <AnimatedButton variant="secondary" size="xl" onClick={() => setSheetOpen(true)} aria-label={t("show_referees_count", {
      count: total
    })} aria-haspopup="dialog" aria-expanded={sheetOpen}><Icon icon={ExpandIcon} /></AnimatedButton> : null;
    $[147] = sheetOpen;
    $[148] = showReferees;
    $[149] = t;
    $[150] = total;
    $[151] = t43;
  } else {
    t43 = $[151];
  }
  let t44;
  if ($[152] !== userId) {
    t44 = {
      kind: "referral_link",
      userId
    };
    $[152] = userId;
    $[153] = t44;
  } else {
    t44 = $[153];
  }
  let t45;
  if ($[154] !== tSocial) {
    t45 = tSocial("share_prompt");
    $[154] = tSocial;
    $[155] = t45;
  } else {
    t45 = $[155];
  }
  let t46;
  if ($[156] !== tSocial) {
    t46 = tSocial("share_modal_title");
    $[156] = tSocial;
    $[157] = t46;
  } else {
    t46 = $[157];
  }
  let t47;
  if ($[158] !== t) {
    t47 = t("invite_cta");
    $[158] = t;
    $[159] = t47;
  } else {
    t47 = $[159];
  }
  let t48;
  if ($[160] !== t47) {
    t48 = <AnimatedButton variant="secondary" size="xl" className="flex-1">{t47}</AnimatedButton>;
    $[160] = t47;
    $[161] = t48;
  } else {
    t48 = $[161];
  }
  let t49;
  if ($[162] !== t44 || $[163] !== t45 || $[164] !== t46 || $[165] !== t48 || $[166] !== url) {
    t49 = <ShareModal surface={t44} url={url} text={t45} title={t46} trigger={t48} />;
    $[162] = t44;
    $[163] = t45;
    $[164] = t46;
    $[165] = t48;
    $[166] = url;
    $[167] = t49;
  } else {
    t49 = $[167];
  }
  let t50;
  if ($[168] !== availableToClaim || $[169] !== claimable || $[170] !== formatD6 || $[171] !== t) {
    t50 = claimable ? <AnimatedButton variant="primary" size="xl" className="flex-1">{t("claim_cta", {
        amount: formatD6(availableToClaim)
      })}</AnimatedButton> : null;
    $[168] = availableToClaim;
    $[169] = claimable;
    $[170] = formatD6;
    $[171] = t;
    $[172] = t50;
  } else {
    t50 = $[172];
  }
  let t51;
  if ($[173] !== t43 || $[174] !== t49 || $[175] !== t50) {
    t51 = <div className="mt-auto flex items-center gap-2">{t43}{t49}{t50}</div>;
    $[173] = t43;
    $[174] = t49;
    $[175] = t50;
    $[176] = t51;
  } else {
    t51 = $[176];
  }
  let t52;
  if ($[177] !== t13 || $[178] !== t18 || $[179] !== t19 || $[180] !== t42 || $[181] !== t51) {
    t52 = <Panel className={PANEL_HEIGHT_CLASS}><PanelContent className="flex h-full flex-col gap-4">{t13}{t18}{t19}{t42}{t51}</PanelContent></Panel>;
    $[177] = t13;
    $[178] = t18;
    $[179] = t19;
    $[180] = t42;
    $[181] = t51;
    $[182] = t52;
  } else {
    t52 = $[182];
  }
  let t53;
  if ($[183] !== sheetOpen || $[184] !== total || $[185] !== userId) {
    t53 = <ShowRefereesDialog userId={userId} count={total} open={sheetOpen} onOpenChange={setSheetOpen} />;
    $[183] = sheetOpen;
    $[184] = total;
    $[185] = userId;
    $[186] = t53;
  } else {
    t53 = $[186];
  }
  let t54;
  if ($[187] !== helpOpen) {
    t54 = <ReferralsHowItWorksDialog open={helpOpen} onOpenChange={setHelpOpen} />;
    $[187] = helpOpen;
    $[188] = t54;
  } else {
    t54 = $[188];
  }
  let t55;
  if ($[189] !== t52 || $[190] !== t53 || $[191] !== t54) {
    t55 = <>{t52}{t53}{t54}</>;
    $[189] = t52;
    $[190] = t53;
    $[191] = t54;
    $[192] = t55;
  } else {
    t55 = $[192];
  }
  return t55;
}